import React from "react";
import PropTypes from "prop-types";
import Popup from "../../components/Popup";
import styled from "styled-components";
import Constants from "../../submodules/logictry_config/constants";
import Navigation from "../../services/Navigation";
import UserCache from "../../services/cache/UserCache";
import TreeCache from "../../services/cache/TreeCache";
import { AccountSpreadsheetsOpen, AccountTemplatesOpen } from "../../services/Pagination/index";
import { TreeJsonToTreeText, TreeTextToTreeJson } from "../../models/tree";
import Wysiwyg from "../../submodules/logictry_wysiwyg/src/js";
import { convertStructuredTextIntoLogicSyntax, convertLogicSyntaxIntoStructuredText } from "../../submodules/logictry_wysiwyg/src/utils";
import GoInteractive from "../../components/GoInteractive";
import { ArrowLeftIcon } from "../../styledhtml/Icon";
import TreeApi from '../../services/server/TreeApi';
import DatabaseApi from '../../services/server/DatabaseApi';
import CustomAppsApi from '../../services/server/CustomAppsApi';
import DatabaseCollectionApi from '../../services/server/DatabaseCollectionApi';
import ContentSourceField from '../../components/ContentSourceField';
import UserAccount from '../../services/UserAccount';
import { getWysiwygImagePasteOptions } from '../../utils/imagePaste';
import Company from '../../services/Company';
import AppConstants from '../../submodules/logictry_config/s3_config.json';
import LogicBaseCollectionCache from '../../services/cache/LogicBaseCollectionCache';
import { isUrlValid, isColorInvalid, isPasswordInvalid } from '../../utils/regex';
import ColorPicker from '../../components/ColorPicker';
import convertColorToHex from '../../utils/convertcolortohex';
import { getTitleDescriptionAndImage } from '../../utils/parseexternalcontent';
import { validateContentSourceUrl } from '../../utils/checkContentType';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
import { extractCleanText } from '../../services/OpenAI';
import CreateContentPopupService from '../../services/CreateContentPopup';
import LogicBaseLiveStreamApi from '../../services/server/LogicBaseLiveStreamApi';

import {
  Card,
  Title,
  SectionTitle,
  SelectField,
  DialogActions,
  ConfirmButton,
  CancelButton,
  Form,
  Input,
  InputBlock,
  Label,
  WarningText,
  InlineFieldRow,
  CheckboxRow,
  Details,
  Summary,
  SettingsButton,
  TextArea
} from "../../styledhtml/Tailwind";
import AssetsPopup from "../../components/AssetsPopup";
import { CONTENT_TEMPLATES, groupContentTemplatesByCategory } from "./contentTemplates";

const WysiwygContainer = styled.div`
  overflow: hidden;
  height: 500px;
  max-height: calc(100vh - 12rem);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const GenerationOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: white;
  display: flex;
  flex-direction: column;
`;

const OverlayHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 2.5rem;
`;
const Template = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  min-width: 360px;
  ${Constants.MediaMobile} {
    min-width: unset;
  }
`;

const TemplateTextArea = styled(TextArea)`
  min-height: 220px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
  &::placeholder {
    font-family: Montserrat, Raleway, "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-size: ${Constants.SmallFontSize};
    font-weight: 400;
  }
`;

const CREATE_OPTIONS = [
  {
    value: 'blank',
    name: 'Blank',
    prompt: ''
  },
  {
    value: 'existing',
    name: 'Share Content',
    prompt: ''
  },
  {
    value: 'create',
    name: 'Create Content',
    prompt: '<h1>Start from a blank page</h1><div><p>This is a clean starting point. Write freely, or open <b>Use Existing Templates</b> to pick a designed layout.</p></div>'
  },
  {
    value: 'paste',
    name: 'Create Content from Template',
    prompt: ''
  },
  {
    value: 'generate',
    name: 'Generate Content with AI',
    prompt: 'Put in your form...'
  },
];

const CUSTOMAPP_CREATE_OPTION = {
  value: 'customapp',
  name: 'Deploy Custom App',
  prompt: '',
};

const LIVEWEBINAR_CREATE_OPTION = {
  value: 'livewebinar',
  name: 'Meeting / Live Stream',
  prompt: '',
};

const startCreateOption = (value) => (value && value !== 'choose' ? value : 'blank');

const TEMPLATE_PARSE_ERROR = 'The template could not be parsed.';

function templateLineParts(line) {
  const match = String(line).match(/^(\s*)\[([^\]]*)\](.*)$/);
  if (!match) return null;
  return {
    indent: match[1],
    attributes: match[2],
    attribute: match[2].split(',')[0].trim(),
    text: match[3].trim(),
  };
}

function readTemplateFields(raw) {
  const text = (raw || '').trim();
  if (!text) return { empty: true, title: '', description: '' };
  let json;
  try {
    json = TreeTextToTreeJson(text);
  } catch {
    return null;
  }
  const children = json && Array.isArray(json.children) ? json.children : [];
  if (!children.some((child) => child && child.attributes && child.attributes[0])) return null;
  const titleNode = children.find((child) => child.attributes && child.attributes[0] === 'Title');
  const descNode = children.find((child) => child.attributes && child.attributes[0] === 'Description');
  return {
    empty: false,
    title: titleNode ? String(titleNode.text || '') : '',
    description: descNode ? String(descNode.text || '') : '',
  };
}

function upsertTemplateField(raw, attribute, value) {
  const source = raw || '';
  const newline = source.includes('\r\n') ? '\r\n' : '\n';
  const lines = source.split(/\r?\n/);
  const rendered = String(value || '').replace(/\r?\n/g, '\\n');
  const index = lines.findIndex((line) => {
    const parts = templateLineParts(line);
    return parts && parts.attribute === attribute;
  });
  if (index >= 0) {
    const parts = templateLineParts(lines[index]);
    lines[index] = `${parts.indent}[${parts.attributes}]${rendered ? ` ${rendered}` : ''}`;
    return lines.join(newline);
  }
  const fresh = `[${attribute}]${rendered ? ` ${rendered}` : ''}`;
  if (!source.trim()) return fresh;
  if (attribute === 'Description') {
    const titleIndex = lines.findIndex((line) => {
      const parts = templateLineParts(line);
      return parts && parts.attribute === 'Title';
    });
    if (titleIndex >= 0) {
      lines.splice(titleIndex + 1, 0, fresh);
      return lines.join(newline);
    }
  }
  lines.unshift(fresh);
  return lines.join(newline);
}

function templateWithFields(raw, title, description) {
  return upsertTemplateField(upsertTemplateField(raw, 'Title', title), 'Description', description);
}

function templateTextToTree(raw, title, description) {
  const text = (raw || '').trim();
  if (!text) return { error: TEMPLATE_PARSE_ERROR };
  let json;
  try {
    json = TreeTextToTreeJson(text);
  } catch {
    return { error: TEMPLATE_PARSE_ERROR };
  }
  const children = json && Array.isArray(json.children) ? json.children : [];
  if (!children.some((child) => child && child.attributes && child.attributes[0])) {
    return { error: TEMPLATE_PARSE_ERROR };
  }

  const titleNode = children.find((child) => child.attributes && child.attributes[0] === 'Title');
  const descNode = children.find((child) => child.attributes && child.attributes[0] === 'Description');
  if (title) {
    if (titleNode) titleNode.text = title;
    else children.unshift({ text: title, attributes: ['Title'], children: [] });
  }
  if (description) {
    if (descNode) descNode.text = description;
    else {
      const titleIndex = children.findIndex((child) => child.attributes && child.attributes[0] === 'Title');
      children.splice(titleIndex >= 0 ? titleIndex + 1 : 0, 0, {
        text: description,
        attributes: ['Description'],
        children: [],
      });
    }
  }

  const resolvedTitle = String(title || (titleNode && titleNode.text) || '').trim();
  if (!resolvedTitle) return { error: 'Title is missing' };
  if (!title && !description) return { text };
  return { text: TreeJsonToTreeText(json) };
}

const getCreateOptions = (collection) => {
  if (!UserAccount.isDevAccount) return CREATE_OPTIONS;
  const opts = [...CREATE_OPTIONS, CUSTOMAPP_CREATE_OPTION];
  if (collection?._id) opts.push(LIVEWEBINAR_CREATE_OPTION);
  return opts;
};

const GENERATE_OPTIONS = [
  {
    value: 'intakeform',
    name: 'Generate Intake Form',
    prompt: `<b>[EXAMPLE - MODIFY ME]</b><br><br> <b>Client Intake & Lead Qualification Form</b><br> Please answer the following questions so we can better understand your needs and determine if there is a strong fit to work together.<br><br> <b>Step 1: Understanding Your Situation</b> <ul> <li>What best describes your current situation?<br> A. Exploring options / early research<br> B. Actively evaluating solutions<br> C. Ready to move forward soon<br> D. Urgent need / immediate support required </li> <li>In your own words, what challenge are you trying to solve?<br> [Open Text] </li> <li>What outcome would success look like for you?<br> [Open Text] </li> </ul> <b>Step 2: Type of Support Needed</b> <ul> <li>What type of support are you looking for?<br> A. Advisory / consultation<br> B. Hands-on execution<br> C. Ongoing partnership<br> D. Not sure yet </li> <li>Is there anything specific you are looking for in an expert or partner?<br> [Open Text] </li> </ul> <b>Step 3: Qualification</b> <ul> <li>What is your expected budget range?<br> A. Under $1,000<br> B. $1,000 – $5,000<br> C. $5,000 – $25,000<br> D. $25,000+ </li> <li>What is your expected timeline?<br> A. Just exploring (no timeline)<br> B. Within the next 3 months<br> C. Within the next month<br> D. Immediately </li> </ul> <b>Step 4: Additional Context</b> <ul> <li>Have you tried any solutions before? If so, what worked or didn’t?<br> [Open Text] </li> <li>Is there anything else we should know to better support you?<br> [Open Text] </li> </ul>`
  },
  {
    value: 'directory',
    name: 'Generate Directory (intake + spreadsheet + listing)',
    prompt: `<b>[EXAMPLE - MODIFY ME]</b><br><br><b>Community / event directory</b><br>Describe who should be listed, what fields appear on each profile card, and how visitors should filter or search (e.g. sector, role, location). List the intake questions you want (with answer types: open text, single choice, multi-select). We will generate the spreadsheet, the public intake form, and the browsable directory in one step.<br><br><b>Audience:</b> [who submits + who browses]<br><b>Profile fields:</b> [bullets]<br><b>Filters:</b> [which fields are dropdown filters on the directory]`,
  },
  {
    value: 'aipoweredlearningchat',
    name: 'Generate AI-Powered Learning Chat',
    prompt: `<b>[EXAMPLE - MODIFY ME]</b> <br> <br>Electric Vehicles AI-Powered Learning Chat <br>Answer these questions to receive a tailored AI-powered learning path that helps you understand electric vehicles and make informed purchase or usage decisions. <br> <br>First, use the following questions: <br> <ul> <li>What is your current knowledge of electric vehicles and their technology?</li> <li>Are you more interested in performance, environmental impact, or cost savings?</li> <li>Do you prefer to learn through reading, interactive activities, or videos?</li> </ul>Second, add 3 more questions and include 4 answer choices to each question. <br> <br>Next,&nbsp;provide me with a personalized learning journey for electric vehicles based upon my answers to your questions.`
  },
  {
    value: 'aipowereddecisionchat',
    name: 'Generate AI-Powered Decision Chat',
    prompt: '<b>[EXAMPLE - MODIFY ME]</b> <br> <br>Car Purchase AI-Powered Decision Chat <br>Answer these questions to receive a specific AI-powered recommendation for your next car purchase. <br> <br>First, use the following questions: <br> <ul> <li>What type of car are you interested in (sedan, SUV, electric, hybrid, etc.)? </li> <li>Do you prioritize fuel efficiency, performance, or luxury features? </li> <li>What is your budget range for purchasing a car? </li> </ul> Second, add 3 more questions and include 4 answer choices to each question. <br> <br>Next, provide me with 3 specific recommendations for my car purchase based upon my answers to your questions.'
  },
  {
    value: 'aipowereddialoguechat',
    name: 'Generate AI-Powered Dialogue Chat',
    prompt: `<b>[EXAMPLE - MODIFY ME]</b> <br> <br>Electric vs Hybrid vs Gas Vehicles AI-Powered Dialogue Chat <br>Answer these questions to engage in a live, constructive AI-powered dialogue that helps you explore multiple perspectives on vehicle choice and energy sources. <br> <br>First, use the following questions: <br> <ul> <li>What factors are most important to you when choosing a vehicle (e.g., cost, performance, environmental impact)?</li> <li>What do you see as the main benefits or drawbacks of electric vehicles compared to hybrids and gas vehicles?</li> <li>How do infrastructure, such as charging stations or fuel availability, influence your vehicle preferences?</li> <li>How do long-term costs, including maintenance and fuel, affect your decision between electric, hybrid, and gas vehicles? <br> </li> </ul> <div>Second, add 3 more questions and include 4 answer choices to each question.</div> <div> <br> </div>Next, engage in a live dialogue with me presenting multiple perspectives on Electric vs Hybrid vs Gas Vehicles and how they differ with my views based on my answers to your questions.`
  },
  {
    value: 'analyzetext',
    name: 'Suggest AI-Powered Chats from Text',
    prompt: '<b>[EXAMPLE - MODIFY ME] <br> </b> <br>Electric, Hybrid, or Gas: Choosing Vehicles in a Renewable Future <br> <br>Explore the trade-offs between electric, hybrid, and gas vehicles in the context of our energy transition. This discussion examines environmental impact, performance, and long-term sustainability, while considering the role of renewable energy versus fossil fuels. Engage in an AI-powered dialogue to weigh options, uncover hidden costs, and discover which vehicle choice aligns best with both your needs and a cleaner energy future.'
  },
];
const VIEW_OPTIONS = [
  {
    value: 'LinkView',
    name: 'Anyone with the URL can view',
  },
  {
    value: 'Private',
    name: 'Only subscribers and users invited can view',
  }
]
const EMBED_OPTIONS = [
  {
    value: 'Enabled',
    name: 'Embed is enabled',
  },
  {
    value: 'Disabled',
    name: 'Embed is disabled',
  }
];

export default class CreateTemplatePopup extends LogictryComponent {
  static propTypes = {
    treeOrSpreadsheet: PropTypes.object,
    team: PropTypes.string,
    collection: PropTypes.object,
    onClose: PropTypes.func,
    initialCreateOption: PropTypes.string,
    openTemplatesOnMount: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    const { collection, initialCreateOption } = props;
    this.state = {
      html: {},
      createOption: startCreateOption(initialCreateOption),
      templateText: '',
      generateOption: "intakeform",
      generating: null,
      title: '',
      description: '',
      password: '',
      viewOption: 'LinkView',
      embedOption: 'Enabled',
      url: '',
      image: '',
      backgroundColor: '',
      color: (collection && collection.color) || '',
      error: '',
      convertHeaders: false,
      customApps: [],
      selectedCustomApp: '',
      databases: [],
      selectedDatabase: '',
      databaseReadiness: null,
      customAppSettings: {},
      dbCollections: [],
      collectionMap: {},
      loadingDbCollections: false,
      creatingCustomApp: false,
      creatingLiveWebinar: false,
    };
  }

  componentDidMount() {
    UserCache.onStateUpdate(this);
    TreeCache.onStateUpdate(this);
    this.loadCustomAppSetup();
    if (this.props.initialCreateOption && !this._initialCreateBootstrapped) {
      this._initialCreateBootstrapped = true;
      this.changeCreateOption(startCreateOption(this.props.initialCreateOption));
    }
    if (this.props.openTemplatesOnMount) {
      this.setState({ showChooseTemplatePopup: true });
    }
  }

  componentWillUnmount() {
    UserCache.offStateUpdate(this);
    TreeCache.offStateUpdate(this);
    if (this.wysiwyg) {
      this.wysiwyg.unmount();
      this.wysiwyg = null;
    }
  }

  componentDidUpdate() {
    if (this._seededFromPrev) return;
    const { createOption, html } = this.state;
    if (createOption !== 'create') return;
    const prevHtml = this.getPreviousPostHtml();
    if (!prevHtml) return;
    const createOpt = getCreateOptions(this.props.collection).find((o) => o.value === 'create');
    const current = html['create'];
    const untouched = !current || current === (createOpt?.prompt || "");
    if (!untouched) return;
    this._seededFromPrev = true;
    if (this.wysiwyg) this.wysiwyg.changeText(prevHtml);
    this.state.html['create'] = prevHtml;
    this.forceUpdate();
  }

  getPreviousPostHtml = () => {
    const { collection } = this.props;
    const id = collection?.apps?.[0];
    if (!id) return null;
    const prev = TreeCache.getTree(id);
    if (!prev || prev.error || !prev.getTreeTemplateText) return null;
    const json = TreeTextToTreeJson(prev.getTreeTemplateText());
    const bodyJson = {
      ...json,
      children: (json.children || []).filter((c) => {
        const a = c.attributes?.[0];
        return a !== 'Title' && a !== 'Description';
      }),
    };
    return convertLogicSyntaxIntoStructuredText(bodyJson) || null;
  };

  onRef = (mountNode) => {
    const { createOption, generateOption, html } = this.state;
    const { prompt } = createOption === 'generate' && GENERATE_OPTIONS.find((o) => o.value === generateOption)
      || getCreateOptions(this.props.collection).find((o) => o.value === createOption) || {};

    if (this.wysiwyg || !mountNode) return;

    const option = createOption === 'generate' && generateOption || createOption;
    const previousPostHtml = option === 'create' ? this.getPreviousPostHtml() : null;
    if (previousPostHtml) this._seededFromPrev = true;
    const initial = html[option] || previousPostHtml || prompt || "";
    this.state.html[option] = initial;

    this.wysiwyg = new Wysiwyg(mountNode, {
      fullscreen: true,
      disabled: false,
      initialText: initial,
      onTextChanged: (htmlText) => {
        const { createOption, generateOption } = this.state;
        const option = createOption === 'generate' && generateOption || createOption;
        this.state.html[option] = htmlText;
        this.forceUpdate();
      },
      borderRadius: 8,
      imagePaste: getWysiwygImagePasteOptions(),
    });
  };

  changeCreateOption = (value) => {
    const next = startCreateOption(value);
    const { html } = this.state;
    const opt = getCreateOptions(this.props.collection).find((o) => o.value === next);
    const previousPostHtml = next === 'create' ? this.getPreviousPostHtml() : null;
    const nextText = html[next] || previousPostHtml || (opt && opt.prompt) || "";
    if (previousPostHtml && !html[next]) this._seededFromPrev = true;

    if (this.wysiwyg) {
      this.wysiwyg.changeText(nextText);
    }

    this.state.html[next] = nextText;
    const patch = { createOption: next, error: '' };
    if (next === 'paste') {
      const fields = readTemplateFields(this.state.templateText);
      if (fields && !fields.empty) {
        patch.title = fields.title;
        patch.description = fields.description;
      } else if (!(this.state.templateText || '').trim() && (this.state.title || this.state.description)) {
        patch.templateText = templateWithFields('', this.state.title, this.state.description);
      }
    }
    this.setState(patch, () => {
      if (next === 'customapp') this.loadCustomAppCollections();
    });
    if (next === 'generate') this.changeGenerateOption(this.state.generateOption);
  };
  changeGenerateOption = (value) => {
    const { html } = this.state;
    const opt = GENERATE_OPTIONS.find((o) => o.value === value);
    const nextText = html[value] || opt.prompt || "";

    if (this.wysiwyg) {
      this.wysiwyg.changeText(nextText);
    }

    this.state.html[value] = nextText;
    this.setState({ generateOption: value, error: '' });
  };

  clearPrompt = () => {
    const { createOption } = this.state;
    const { prompt } = getCreateOptions(this.props.collection).find((o) => o.value === createOption) || {};

    if (this.wysiwyg) {
      this.wysiwyg.changeText(prompt || "");
    }

    this.state.html[createOption] = prompt || "";
    this.forceUpdate();
  };

  loadCustomAppSetup = async () => {
    try {
      const company = Company.company?._id || '';
      const [customApps, databases] = await Promise.all([
        CustomAppsApi.query({ state: 'Live', count: 100 }),
        DatabaseApi.query({ state: 'Live', company, count: 100 }),
      ]);
      const selectedCustomApp = customApps?.[0]?._id || '';
      this.setState({
        customApps: customApps || [],
        selectedCustomApp,
        customAppSettings: this.getCustomAppDefaultSettings(selectedCustomApp, customApps || []),
        databases: databases || [],
        selectedDatabase: databases?.[0]?._id || '',
        collectionMap: {},
        dbCollections: [],
      }, () => { if (this.state.createOption === 'customapp') this.loadCustomAppCollections(); });
    } catch {
      this.setState({ error: 'Custom apps could not be loaded.' });
    }
  }

  loadDatabaseReadiness = async () => {
    const { createOption, selectedCustomApp, selectedDatabase, customAppSettings, dbCollections } = this.state;
    if (createOption !== 'customapp' || !selectedCustomApp || !selectedDatabase) return;
    if (!dbCollections || !dbCollections.length) {
      this.setState({ databaseReadiness: { ready: false, missing: [] } });
      return;
    }
    this.setState({ databaseReadiness: null });
    try {
      const collectionMapPayload = this.buildCollectionMapPayload();
      if (Object.keys(collectionMapPayload).length === 0) {
        this.setState({ databaseReadiness: { ready: false, missing: [] } });
        return;
      }
      const databaseReadiness = await CustomAppsApi.checkReadiness({
        customApp: selectedCustomApp,
        database: selectedDatabase,
        collectionMap: collectionMapPayload,
        settings: customAppSettings,
      });
      this.setState({ databaseReadiness });
    } catch {
      this.setState({ databaseReadiness: { ready: false, error: 'ReadinessCheckFailed' } });
    }
  }

  setCustomAppSetting = (key, value) => {
    this.setState(
      {
        customAppSettings: {
          ...this.state.customAppSettings,
          [key]: value,
        },
        error: '',
      },
      () => {
        const { dbCollections, collectionMap } = this.state;
        const customApp = this.getSelectedCustomApp();
        const version = this.getActiveCustomAppVersion(customApp);
        const nextMap = this.suggestCollectionMap(version, dbCollections, collectionMap);
        this.setState({ collectionMap: nextMap }, this.loadDatabaseReadiness);
      },
    );
  }

  suggestCollectionMap = (version, list, previousMap) => {
    const m = version.resourceManifest && version.resourceManifest.collections;
    if (!m) return { ...(previousMap || {}) };
    if (!list || !list.length) return { ...(previousMap || {}) };
    const { customAppSettings } = this.state;
    const next = { ...previousMap };
    Object.keys(m).forEach((k) => {
      const meta = m[k];
      if (meta.optional && !customAppSettings[`${k}Enabled`]) {
        delete next[k];
        return;
      }
      const wantName = meta.name;
      const prev = next[k];
      if (prev && list.some((c) => String(c._id) === String(prev))) {
        return;
      }
      const found = list.find((c) => c.name === wantName);
      if (found) next[k] = String(found._id);
      else if (!prev) next[k] = '';
    });
    return next;
  }

  buildCollectionMapPayload = () => {
    const { collectionMap, customAppSettings } = this.state;
    const version = this.getActiveCustomAppVersion();
    const m = version.resourceManifest && version.resourceManifest.collections;
    if (!m) return {};
    const out = {};
    Object.keys(m).forEach((key) => {
      const meta = m[key];
      if (meta.optional && !customAppSettings[`${key}Enabled`]) return;
      const id = collectionMap[key];
      if (id) out[key] = id;
    });
    return out;
  }

  loadCustomAppCollections = async () => {
    const { createOption, selectedCustomApp, selectedDatabase } = this.state;
    if (createOption !== 'customapp' || !selectedCustomApp || !selectedDatabase) return;
    this.setState({ loadingDbCollections: true, databaseReadiness: null, error: '' });
    try {
      const list = await DatabaseCollectionApi.query({ database: selectedDatabase, state: 'Live', count: 500 });
      const customApp = this.getSelectedCustomApp();
      const version = this.getActiveCustomAppVersion(customApp);
      const collectionMap = this.suggestCollectionMap(version, list || [], this.state.collectionMap);
      this.setState(
        { dbCollections: list || [], collectionMap, loadingDbCollections: false },
        this.loadDatabaseReadiness,
      );
    } catch {
      this.setState({ loadingDbCollections: false, databaseReadiness: { ready: false, error: 'CollectionsLoadFailed' } });
    }
  }

  getSelectedCustomApp = () => {
    const { customApps, selectedCustomApp } = this.state;
    return customApps.find((app) => app._id === selectedCustomApp);
  }

  getActiveCustomAppVersion = (customApp = this.getSelectedCustomApp()) => {
    const versions = customApp?.versions || [];
    return versions.find(({ version }) => version === customApp.currentVersion) || versions[0] || {};
  }

  getExplicitCustomAppSettingFields = (version) => {
    const fields = version.settingsSchema || version.settingFields || version.settingsFields || version.settings || [];
    if (Array.isArray(fields)) return fields;
    return Object.keys(fields).map((key) => (
      fields[key] && typeof fields[key] === 'object'
        ? { key, ...fields[key] }
        : { key, default: fields[key] }
    ));
  }

  getInferredCustomAppSettingFields = (version) => {
    const fields = [];
    const addField = (field) => {
      if (!field.key || fields.find(({ key }) => key === field.key)) return;
      fields.push(field);
    };
    const documents = version.resourceManifest?.documents
      ? Object.keys(version.resourceManifest.documents).map((key) => ({ key, ...version.resourceManifest.documents[key] }))
      : version.starterDocuments || [];
    documents.forEach((document) => {
      Object.keys(document.fields || {}).forEach((fieldKey) => {
        const definition = document.fields[fieldKey];
        if (!definition || typeof definition !== 'object' || !definition.from) return;
        if (!definition.from.startsWith('settings.')) return;
        const key = definition.from.replace('settings.', '');
        addField({
          key,
          label: this.getCustomAppSettingLabel(key),
          type: definition.type || 'String',
          required: !Object.hasOwnProperty.call(definition, 'default'),
          default: definition.default,
        });
      });
    });
    const collections = version.resourceManifest?.collections
      ? Object.keys(version.resourceManifest.collections).map((key) => ({ key, ...version.resourceManifest.collections[key] }))
      : version.requiredCollections || [];
    collections.forEach((collection) => {
      if (!collection.optional) return;
      const key = `${collection.key}Enabled`;
      addField({
        key,
        label: collection.label || collection.title || `Enable ${this.getCustomAppSettingLabel(collection.key)}`,
        type: 'Boolean',
        default: false,
      });
    });
    return fields;
  }

  getCustomAppSettingFields = (version = this.getActiveCustomAppVersion()) => {
    const explicitFields = this.getExplicitCustomAppSettingFields(version);
    return explicitFields.length > 0 ? explicitFields : this.getInferredCustomAppSettingFields(version);
  }

  getCustomAppSettingLabel = (key) => (
    String(key || '')
      .replace(/([A-Z])/g, ' $1')
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim()
  )

  getCustomAppSettingDefault = (field) => {
    if (Object.hasOwnProperty.call(field, 'default')) return field.default;
    if (field.key === 'timeZone') return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
    if (field.type === 'Boolean' || field.type === 'checkbox') return false;
    return '';
  }

  getCustomAppDefaultSettings = (selectedCustomApp, customApps = this.state.customApps) => {
    const customApp = customApps.find((app) => app._id === selectedCustomApp);
    const version = this.getActiveCustomAppVersion(customApp);
    const settings = {};
    this.getCustomAppSettingFields(version).forEach((field) => {
      if (field.key) settings[field.key] = this.getCustomAppSettingDefault(field);
    });
    return settings;
  }

  renderCustomAppSettingField = (field) => {
    const key = field.key || field.name;
    if (!key) return null;
    const { customAppSettings } = this.state;
    const value = customAppSettings[key];
    const label = field.label || field.title || this.getCustomAppSettingLabel(key);
    const type = field.inputType || field.type || 'String';
    const options = field.options || field.values;
    const setValue = (nextValue) => this.setCustomAppSetting(key, nextValue);
    if (type === 'Boolean' || type === 'checkbox') {
      return (
        <CheckboxRow key={key}>
          <input
            type="checkbox"
            checked={!!value}
            onChange={() => setValue(!value)}
          />
          {label}
        </CheckboxRow>
      );
    }
    return (
      <InputBlock key={key}>
        <Label>{label}</Label>
        {options ? (
          <SelectField
            value={value || ''}
            onChange={(e) => setValue(e.target.value)}
          >
            {options.map((option) => {
              const optionValue = Object.hasOwnProperty.call(option, 'value') ? option.value : option;
              const optionLabel = option.label || option.name || optionValue;
              return <option key={optionValue} value={optionValue}>{optionLabel}</option>;
            })}
          </SelectField>
        ) : (
          <Input
            type={type === 'Date' ? 'datetime-local' : type === 'Number' ? 'number' : type === 'String' ? 'text' : type}
            value={value || ''}
            maxLength={field.maxLength || 1000}
            placeholder={field.placeholder || ''}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
      </InputBlock>
    );
  }

  renderCustomAppCollectionMappers = () => {
    const version = this.getActiveCustomAppVersion();
    const m = version.resourceManifest && version.resourceManifest.collections;
    if (!m) return null;
    const { dbCollections, collectionMap, loadingDbCollections, customAppSettings } = this.state;
    if (loadingDbCollections) {
      return (
        <InputBlock>
          <Label>Database collections</Label>
          <div style={{ fontSize: Constants.SmallFontSize, color: '#666' }}>Loading collections…</div>
        </InputBlock>
      );
    }
    if (!dbCollections.length) {
      return <WarningText>No collections in this database. Add collections in Data, then try again.</WarningText>;
    }
    return Object.keys(m).map((key) => {
      const meta = m[key];
      if (meta.optional && !customAppSettings[`${key}Enabled`]) return null;
      const label = meta.title || meta.name || this.getCustomAppSettingLabel(key);
      return (
        <InputBlock key={key}>
          <Label>Collection: {label}</Label>
          <SelectField
            value={collectionMap[key] || ''}
            onChange={(e) => {
              const v = e.target.value;
              this.setState(
                { collectionMap: { ...this.state.collectionMap, [key]: v }, error: '' },
                this.loadDatabaseReadiness,
              );
            }}
          >
            <option value="">Select a collection…</option>
            {dbCollections.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
                {c.title && c.title !== c.name ? ` — ${c.title}` : ''}
              </option>
            ))}
          </SelectField>
        </InputBlock>
      );
    });
  }

  renderCustomAppSettings = () => {
    const fields = this.getCustomAppSettingFields();
    if (fields.length === 0) return null;
    return <>{fields.map(this.renderCustomAppSettingField)}</>;
  }

  createTree = (text, url) => {
    const { collection, onClose, team } = this.props;
    const { color, image, password, viewOption, embedOption } = this.state;

    const tree = collection ? {
      type: 'Template',
      version: 1,
      owner: UserAccount.account._id,
      userLoggedIn: UserAccount.account._id,
      children: { text },
    } : TreeCache.createNew(text);

    const hexColor = color ? convertColorToHex(color) : null;
    if (hexColor || image) tree.background = {};
    if (hexColor) tree.background.color = hexColor;
    if (image) tree.background.image = image;
    if (password) tree.password = password;
    if (viewOption) tree.shareSetting = viewOption;
    if (embedOption) tree.embedSetting = embedOption;
    if (url) tree.customSource = { url };

    if (collection) {
      return TreeApi.createTree(tree).then((results) => {
        if (!results || !results._id || results.error) return this.setState({ error: 'Something went wrong' });
        collection.apps.unshift(results._id);
        LogicBaseCollectionCache.update(collection);
        Navigation.push(`${Navigation.content}/${results._id}?collection=${collection._id}`);
        onClose();
      });
    } else {
      if (team) tree.addTeam(team);
      TreeCache.createTree(tree);
    }
  }
  existingContent = () => {
    const { title, description, url } = this.state;

    const r = validateContentSourceUrl(url, { required: true });
    if (!r.ok) return this.setState({ error: r.error });

    const text = TreeJsonToTreeText({
      children: [
        { text: title, attributes: ['Title'], children: [] },
        { text: description, attributes: ['Description'], children: [] },
        { text: '', attributes: ['Root'], children: [] },
      ],
    });
    this.createTree(text, r.absoluteUrl);
  };
  createBlank = () => {
    const { title, description } = this.state;
    const text = TreeJsonToTreeText({
      children: [
        { text: title, attributes: ['Title'], children: [] },
        { text: description, attributes: ['Description'], children: [] },
        { text: '', attributes: ['Root'], children: [] },
      ],
    });
    this.createTree(text);
  };
  onTitleChange = (e) => {
    const title = e.target.value;
    if (this.state.createOption !== 'paste') {
      this.setState({ title, error: '' });
      return;
    }
    this.setState({
      title,
      error: '',
      templateText: upsertTemplateField(this.state.templateText, 'Title', title),
    });
  };
  onDescriptionChange = (e) => {
    const description = e.target.value.replace(/\n/g, '\\n');
    if (this.state.createOption !== 'paste') {
      this.setState({ description, error: '' });
      return;
    }
    this.setState({
      description,
      error: '',
      templateText: upsertTemplateField(this.state.templateText, 'Description', description),
    });
  };
  onTemplateTextChange = (e) => {
    const templateText = e.target.value;
    const fields = readTemplateFields(templateText);
    const next = { templateText, error: '' };
    if (fields) {
      next.title = fields.title;
      next.description = fields.description;
    }
    this.setState(next);
  };
  pasteTemplate = () => {
    const { templateText, title, description } = this.state;
    const text = templateWithFields(templateText, title, description);
    const result = templateTextToTree(text);
    if (result.error) return this.setState({ error: result.error });
    this.createTree(result.text);
  };
  createContent = () => {
    const { title, description, html, createOption, convertHeaders, backgroundColor } = this.state;

    const attributes = ['Root'];
    if (backgroundColor) attributes.push(`BackgroundColor=${backgroundColor}`);
    const initialJSON = {
      children: [
        { text: title, attributes: ['Title'], children: [] },
        { text: description, attributes: ['Description'], children: [] },
        { text: '', attributes, children: [] },
      ],
    }

    let text = "";
    if (convertHeaders) {
      if (html[createOption]) convertStructuredTextIntoLogicSyntax(html[createOption], initialJSON);
    } else {
      if (html[createOption]) initialJSON.children[2].children.push({ text: html[createOption], attributes: ["Text"], children: [] });
    }
    text = TreeJsonToTreeText(initialJSON);
    this.createTree(text);
  }
  generateContent = () => {
    const { html, createOption, generateOption } = this.state;
    if (createOption !== 'generate') return;
    if (!html[generateOption]) return;
    this.setState({ generating: generateOption });
  }

  createLiveWebinar = async () => {
    const { collection, onClose } = this.props;
    const { title } = this.state;
    if (!collection?._id) {
      return this.setState({ error: 'Create a meeting / Live Stream from a collection page' });
    }

    this.setState({ creatingLiveWebinar: true, error: '' });
    try {
      const res = await LogicBaseLiveStreamApi.devCreate({
        collectionId: collection._id,
        title: (title || 'Meeting / Live Stream').trim().slice(0, 200),
      });
      if (res?.error) {
        return this.setState({ error: res.error, creatingLiveWebinar: false });
      }
      const contentId = res.contentId || res._id;
      if (!contentId) {
        return this.setState({ error: 'Something went wrong', creatingLiveWebinar: false });
      }
      collection.apps.unshift(contentId);
      LogicBaseCollectionCache.update(collection);
      Navigation.push(`${Navigation.content}/${contentId}?collection=${collection._id}`);
      onClose();
    } catch {
      this.setState({ error: 'Something went wrong', creatingLiveWebinar: false });
    }
  };

  createCustomApp = async () => {
    const { collection, onClose } = this.props;
    const {
      selectedCustomApp,
      selectedDatabase,
      databaseReadiness,
      title,
      description,
      color,
      image,
      password,
      viewOption,
      embedOption,
      customAppSettings,
    } = this.state;
    if (!selectedCustomApp) return this.setState({ error: 'Choose a custom app first' });
    if (!selectedDatabase) return this.setState({ error: 'Choose a database first' });
    if (databaseReadiness && databaseReadiness.ready === false) return this.setState({ error: 'This database is not set up for this custom app.' });
    const missingField = this.getCustomAppSettingFields().find((field) => field.required && !customAppSettings[field.key || field.name]);
    if (missingField) return this.setState({ error: `${missingField.label || missingField.title || this.getCustomAppSettingLabel(missingField.key || missingField.name)} is missing` });

    const background = {};
    const hexColor = color ? convertColorToHex(color) : null;
    if (hexColor) background.color = hexColor;
    if (image) background.image = image;

    this.setState({ creatingCustomApp: true, error: '' });
    try {
      const result = await CustomAppsApi.createInstance({
        customApp: selectedCustomApp,
        database: selectedDatabase,
        collectionMap: this.buildCollectionMapPayload(),
        title,
        description,
        collection: collection?._id,
        password,
        shareSetting: viewOption,
        embedSetting: embedOption,
        background: Object.keys(background).length > 0 ? background : undefined,
        settings: customAppSettings,
      });
      if (!result || !result.treeId || result.error) {
        return this.setState({ error: result?.error || 'Something went wrong', creatingCustomApp: false });
      }
      if (result.tree) TreeCache.createFromJson(result.tree);
      AccountTemplatesOpen.initialize();
      AccountTemplatesOpen.emitStateUpdate();
      if (collection) {
        collection.apps.unshift(result.treeId);
        LogicBaseCollectionCache.update(collection);
        Navigation.push(`${Navigation.content}/${result.treeId}?collection=${collection._id}`);
      } else {
        Navigation.push(`${Navigation.content}/${result.treeId}`);
      }
      onClose();
    } catch {
      this.setState({ error: 'Something went wrong', creatingCustomApp: false });
    }
  }

  /** Rich context for AI intake forms: title, short description, plain-text form spec. */
  buildAiGenerationDescription = () => {
    const { generateOption, title, description, html } = this.state;
    const editorHtml = html[generateOption] || '';

    if (generateOption !== 'intakeform' && generateOption !== 'directory') {
      return editorHtml;
    }

    const contentSummary = (description || '').replace(/\\n/g, '\n').trim();
    const sections = [];
    sections.push(`[CONTENT_TITLE]\n${title.trim() || '(Not provided — infer a concise, accurate title from the short description and brief below.)'}`);
    sections.push(`[CONTENT_SHORT_DESCRIPTION]\n${contentSummary || '(Not provided.)'}`);
    const specLabel = generateOption === 'directory' ? '[DIRECTORY_BRIEF]' : '[FORM_SPECIFICATION]';
    sections.push(`${specLabel}\n${extractCleanText(editorHtml) || '(Empty — infer sensible fields from the short description only.)'}`);
    return sections.join('\n\n');
  }

  DIRECTORY_PIPELINE_MARKER = '\n__DIRECTORY_PIPELINE_RESULT__\n';

  completeDirectoryPipeline = (text) => {
    const { collection, onClose } = this.props;
    const marker = this.DIRECTORY_PIPELINE_MARKER;
    const i = text.lastIndexOf(marker);
    if (i < 0) {
      return this.setState({ error: 'Directory generation failed. Try again.', generating: null });
    }
    let payload;
    try {
      payload = JSON.parse(text.slice(i + marker.length).trim());
    } catch {
      return this.setState({ error: 'Directory generation failed. Try again.', generating: null });
    }
    if (payload.error) {
      return this.setState({ error: typeof payload.error === 'string' ? payload.error : 'Something went wrong', generating: null });
    }
    const { spreadsheetId, formTreeId, directoryTreeId } = payload;
    if (!spreadsheetId || !formTreeId || !directoryTreeId) {
      return this.setState({ error: 'Directory generation failed. Try again.', generating: null });
    }
    AccountSpreadsheetsOpen.initialize();
    AccountTemplatesOpen.initialize();
    // AccountSpreadsheetsOpen.emitStateUpdate();
    // AccountTemplatesOpen.emitStateUpdate();

    if (collection) {
      collection.apps.unshift(directoryTreeId);
      LogicBaseCollectionCache.update(collection);
      Navigation.push(`${Navigation.content}/${directoryTreeId}?collection=${collection._id}`);
      onClose();
      return;
    }
    Navigation.push(`${Navigation.content}/${directoryTreeId}`);
    onClose();
  }
  create = () => {
    const { createOption, title, color, image, password } = this.state;

    // Check all valid inputs
    if (color && isColorInvalid(color)) return this.setState({ error: 'Color is invalid' });
    if (image && !isUrlValid(image)) return this.setState({ error: 'Background Image URL is invalid' });
    if (password && isPasswordInvalid(password)) return this.setState({ error: 'Password is invalid' });

    if (createOption === 'paste') return this.pasteTemplate();
    if (!title) return this.setState({ error: 'Title is missing' });
    if (createOption === 'blank') return this.createBlank();
    if (createOption === 'existing') return this.existingContent();
    if (createOption === 'create') return this.createContent();
    if (createOption === 'generate') return this.generateContent();
    if (createOption === 'customapp') return this.createCustomApp();
    if (createOption === 'livewebinar') return this.createLiveWebinar();
  }

  render() {
    const { onClose } = this.props;
    const { collection } = this.props;
    const { generating, html, createOption, generateOption, error, title, description, url, image, backgroundColor, color, password, viewOption, embedOption, convertHeaders, showChooseTemplatePopup, showChooseAssetPopup, customApps, selectedCustomApp, databases, selectedDatabase, databaseReadiness, creatingCustomApp, creatingLiveWebinar, templateText } = this.state;

    const { prompt } = (createOption === 'generate') && GENERATE_OPTIONS.find((o) => o.value === generateOption)
      || getCreateOptions(collection).find((o) => o.value === createOption) || {};
    const resetPromptButton = !html[createOption] || html[createOption] !== prompt;

    let s3Path;
    let signingUrl;

    if (Company.company?._id) {
      s3Path = `${Company.company._id}/${UserAccount.account._id}/`;
      signingUrl = `/v1${AppConstants.S3.signingUrls.companyfiles}`;
    } else {
      s3Path = `${UserAccount.account._id}/`;
      signingUrl = `/v1${AppConstants.S3.signingUrls.userfiles}`;
    }
    this.wysiwyg?.changeBackgroundColor(backgroundColor);

    return (
      <>
        <Popup
          showCloseButton
          allowBackgroundClose={createOption === 'blank'}
          onClose={!generating || generating === "complete" ? onClose : null}
        >
          <Card
            style={{
              maxHeight:
                Constants.isIphone && this.innerHeight
                  ? `calc(${this.innerHeight}px - 6rem - var(--logictry-popup-safe-top, 0px) - var(--logictry-popup-safe-bottom, 0px))`
                  : undefined,
              width: (createOption === 'blank' || createOption === 'paste' || createOption === 'existing' || createOption === 'livewebinar') && 540 || 900,
              height: generating && `calc(100vh - 6rem)` || null,
              overflow: generating && 'hidden' || null,
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Column>
              <Title style={{ textAlign: "center" }}>Create Content</Title>

              <Form>
                <InputBlock>
                  <Label>Title</Label>
                  <Input
                    value={title}
                    maxLength={150}
                    onChange={this.onTitleChange}
                    placeholder="Title"
                  />
                </InputBlock>

                <InputBlock>
                  <Label>Description</Label>
                  <TextArea
                    value={description.replace(/\\n/g, '\n')}
                    maxLength={5000}
                    onChange={this.onDescriptionChange}
                    placeholder="Short description"
                  />
                </InputBlock>

                <InputBlock>
                  <Label>Create Options</Label>
                  <Row>
                    <SelectField
                      value={createOption}
                      onChange={(e) => this.changeCreateOption(e.target.value)}
                    >
                      {getCreateOptions(collection).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.name}
                        </option>
                      ))}
                    </SelectField>
                  </Row>
                </InputBlock>

                {createOption === 'paste' && <InputBlock>
                  <Label>Logictry template</Label>
                  <TemplateTextArea
                    value={templateText}
                    spellCheck={false}
                    onChange={this.onTemplateTextChange}
                    placeholder="Paste template text"
                  />
                </InputBlock>}

                {createOption === 'generate' && <InputBlock>
                  <Label>Generation type</Label>
                  <Row>
                    <SelectField
                      value={generateOption}
                      onChange={(e) => this.changeGenerateOption(e.target.value)}
                    >
                      {GENERATE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.name}
                        </option>
                      ))}
                    </SelectField>
                    <button
                    onClick={this.clearPrompt}
                    style={{
                      opacity: resetPromptButton ? 1 : 0.3,
                      fontSize: Constants.SmallFontSize,
                    }}
                  >
                    Reset Prompt
                  </button>
                  </Row>
                </InputBlock>}

                {createOption === 'customapp' && <>
                  <InputBlock>
                    <Label>App Type</Label>
                    <SelectField
                      value={selectedCustomApp}
                      onChange={(e) => {
                        const nextApp = e.target.value;
                        this.setState({
                          selectedCustomApp: nextApp,
                          customAppSettings: this.getCustomAppDefaultSettings(nextApp),
                          collectionMap: {},
                          error: '',
                        }, this.loadCustomAppCollections);
                      }}
                    >
                      {customApps.length === 0 && <option value="">No custom apps available</option>}
                      {customApps.map((app) => (
                        <option key={app._id} value={app._id}>
                          {app.name}
                        </option>
                      ))}
                    </SelectField>
                  </InputBlock>

                  <InputBlock>
                    <Label>Database</Label>
                    <SelectField
                      value={selectedDatabase}
                      onChange={(e) => this.setState({
                        selectedDatabase: e.target.value,
                        collectionMap: {},
                        error: '',
                      }, this.loadCustomAppCollections)}
                    >
                      {databases.length === 0 && <option value="">No databases available</option>}
                      {databases.map((database) => (
                        <option key={database._id} value={database._id}>
                          {database.title || database.name}
                        </option>
                      ))}
                    </SelectField>
                    {databases.length === 0 && <WarningText>Custom apps require an existing database. Contact Logictry to provision a database for your workspace.</WarningText>}
                    {databaseReadiness && databaseReadiness.ready === false && !databaseReadiness.error && <WarningText>
                      Select a database collection for each role below, or add missing collections in Data. Names in the app definition are only defaults—your choices here are what get deployed.
                    </WarningText>}
                    {databaseReadiness && databaseReadiness.error && <WarningText>Could not verify deployment readiness. Try again.</WarningText>}
                  </InputBlock>

                  {this.renderCustomAppSettings()}
                  {this.renderCustomAppCollectionMappers()}
                </>}

                {/* URL */}
                <div style={{ display: createOption !== 'existing' && 'none' || null }}>

                  <ContentSourceField
                    label="Content URL (Canva, YouTube, etc.)"
                    placeholder="https://..."
                    maxLength={1000}
                    value={url}
                    onChange={async (newUrl) => {
                      this.setState({ url: newUrl, error: '' });
                      const parsed = await getTitleDescriptionAndImage(newUrl);
                      const updates = {};
                      if (parsed.title) updates.title = parsed.title;
                      if (parsed.description) updates.description = parsed.description;
                      if (parsed.image) updates.image = parsed.image;
                      this.setState(updates);
                    }}
                    validateUrlOnBlur={false}
                    s3Path={s3Path}
                    signingUrl={signingUrl}
                    allowFolderUpload={false}
                    onUploadFinish={(fullUrl) => {
                      const clean = fullUrl.signedUrl.split('?')[0];
                      this.setState({ url: clean });
                    }}
                    hideUploadText
                    uploadPickerMode="file"
                  />
                </div>

                <div style={{ display: (createOption !== 'create') && 'none' || null }}>
                  <InputBlock style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
                    <SettingsButton onClick={() => this.setState({ showChooseTemplatePopup: true })}>Use Existing Templates</SettingsButton>
                    <SettingsButton onClick={() => this.setState({ showChooseAssetPopup: true })}>Use Existing or Upload Assets</SettingsButton>
                  </InputBlock>
                </div>

                <div style={{ display: (createOption !== 'create' && createOption !== 'generate') && 'none' || null }}>
                  <InputBlock>
                    {/* <Label>Text Input</Label> */}
                    <WysiwygContainer ref={this.onRef} />
                  </InputBlock>
                </div>

                <Details>
                  <Summary>&nbsp;&nbsp;Advanced Settings</Summary>

                  {(createOption === 'create') && <>
                    <SectionTitle>Content appearance</SectionTitle>

                    <InputBlock>
                      <CheckboxRow>
                        <input
                          type="checkbox"
                          checked={convertHeaders}
                          onChange={() => {
                            this.setState({ convertHeaders: !convertHeaders })
                          }}
                        />
                        Convert headers into collapsible sections?
                      </CheckboxRow>
                    </InputBlock>
                    <InputBlock>
                      <Label>Background Color</Label>
                      <InlineFieldRow>
                        <Input
                          value={backgroundColor}
                          placeholder="Color value"
                          maxLength={20}
                          onChange={(e) => this.setState({ backgroundColor: e.target.value, error: '' })}
                        />

                        <ColorPicker
                          onChange={(c) => this.setState({ backgroundColor: c })}
                          color={backgroundColor ? convertColorToHex(backgroundColor) : Constants.PrimaryColor}
                        />
                      </InlineFieldRow>
                    </InputBlock>
                  </>}

                  <SectionTitle>Thumbnail Appearance</SectionTitle>

                  <InputBlock>
                    <Label>Background Color</Label>
                    <InlineFieldRow>
                      <Input
                        value={color}
                        placeholder="Color value"
                        maxLength={20}
                        onChange={(e) => this.setState({ color: e.target.value, error: '' })}
                      />

                      <ColorPicker
                        onChange={(c) => this.setState({ color: c })}
                        color={color ? convertColorToHex(color) : Constants.PrimaryColor}
                      />
                    </InlineFieldRow>
                  </InputBlock>

                  <ContentSourceField
                    label="Background Image (16:9)"
                    placeholder="Image URL"
                    maxLength={1000}
                    value={image}
                    onChange={(v) => this.setState({ image: v, error: '' })}
                    validateUrlOnBlur={false}
                    s3Path={s3Path}
                    signingUrl={signingUrl}
                    onUploadFinish={(fullUrl) => {
                      const clean = fullUrl.signedUrl.split('?')[0];
                      this.setState({ image: clean });
                    }}
                    hideUploadText
                    uploadPickerMode="imageFile"
                  />

                  <SectionTitle>Visibility</SectionTitle>
                  <InputBlock>
                    <Label>Password</Label>
                    <Input
                      type="text"
                      placeholder="Password"
                      value={password || ''}
                      maxLength={256}
                      onChange={(e) => this.setState({ password: e.target.value })}
                    />
                  </InputBlock>

                  <InputBlock>
                    <Label>View Option</Label>
                    <SelectField
                      value={viewOption}
                      onChange={(e) => this.setState({ viewOption: e.target.value })}
                    >
                      {VIEW_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.name}
                        </option>
                      ))}
                    </SelectField>
                  </InputBlock>

                  <InputBlock>
                    <Label>Embed Option</Label>
                    <SelectField
                      value={embedOption}
                      onChange={(e) => this.setState({ embedOption: e.target.value })}
                    >
                      {EMBED_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.name}
                        </option>
                      ))}
                    </SelectField>
                  </InputBlock>
                </Details>

                {error && <WarningText>{error}</WarningText>}

                <DialogActions>
                  <CancelButton onClick={onClose}>Cancel</CancelButton>
                  <ConfirmButton onClick={this.create}>
                    {(creatingCustomApp || creatingLiveWebinar) ? 'Creating...' : 'Create'}
                  </ConfirmButton>
                </DialogActions>
              </Form>
            </Column>
          </Card>
          {generating && (
            <GenerationOverlay>
              <OverlayHeader>
                {generating === "complete" && (
                  <ArrowLeftIcon
                    style={{ width: 16, height: 16, cursor: "pointer" }}
                    onClick={() => this.setState({ generating: null })}
                  />
                )}
                <Title>
                  {generating === "complete"
                    ? "Suggestions"
                    : generateOption.startsWith("analyze")
                    ? "Generating Suggestions"
                    : generateOption === "directory"
                    ? "Generating Directory"
                    : "Generating Content"}
                </Title>
              </OverlayHeader>

              <div style={{ position: "relative", flex: 1 }}>
                <GoInteractive
                  action={generating}
                  title={title}
                  description={this.buildAiGenerationDescription()}
                  color={color || Constants.PrimaryColor}
                  company={Company.company?._id}
                  onComplete={(text) => {
                    if (generateOption.startsWith('analyze')) this.setState({ generating: "complete" });
                    else if (generateOption === 'directory') this.completeDirectoryPipeline(text);
                    else this.createTree(text);
                  }}
                  onPromptSelect={(text) => {
                    this.wysiwyg.changeText(text || "");

                    const low = text.toLowerCase();
                    const idx = {
                      aipoweredlearningchat: low.indexOf("learning"),
                      aipowereddialoguechat: low.indexOf("dialogue"),
                      aipowereddecisionchat: low.indexOf("decision"),
                    };

                    let next = "aipowereddecisionchat";
                    const found = Object.entries(idx).filter(([, i]) => i !== -1);
                    if (found.length > 0) {
                      next = found.reduce((a, b) => (a[1] < b[1] ? a : b))[0];
                    }

                    this.state.html[next] = text || "";

                    this.setState({
                      generating: null,
                      generateOption: next,
                    });

                    this.forceUpdate();
                  }}
                />
              </div>

            </GenerationOverlay>
          )}
        </Popup>
        {showChooseTemplatePopup && <Popup
          showCloseButton
          allowBackgroundClose
          onClose={() => this.setState({ showChooseTemplatePopup: false })}
        >
          <Card
            style={{
              maxHeight:
                Constants.isIphone && this.innerHeight
                  ? `calc(${this.innerHeight}px - 6rem - var(--logictry-popup-safe-top, 0px) - var(--logictry-popup-safe-bottom, 0px))`
                  : undefined,
              width: '1104px',
              height: generating && `calc(100vh - 6rem)` || null,
              overflow: generating && 'hidden' || null,
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Column>
              <Title style={{ textAlign: "center" }}>Choose Template</Title>
              <Form>
                <InputBlock>
                  <Label>Background Color</Label>
                  <InlineFieldRow>
                    <Input
                      value={backgroundColor}
                      placeholder="Color value"
                      maxLength={20}
                      onChange={(e) => this.setState({ backgroundColor: e.target.value, error: '' })}
                    />

                    <ColorPicker
                      onChange={(c) => this.setState({ backgroundColor: c })}
                      color={backgroundColor ? convertColorToHex(backgroundColor) : Constants.PrimaryColor}
                    />
                  </InlineFieldRow>
                </InputBlock>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
                  {groupContentTemplatesByCategory(CONTENT_TEMPLATES).map(({ category, templates }) => (
                    <div key={category}>
                      <SectionTitle style={{ marginBottom: '0.75rem', fontSize: '0.95rem' }}>{category}</SectionTitle>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                        {templates.map((tmpl) => (
                          <Template key={tmpl.id}>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827' }}>{tmpl.name}</div>
                            <div
                              style={{
                                padding: '1rem',
                                backgroundColor: backgroundColor || '#f3f4f6',
                                width: '100%',
                                height: 420,
                                border: Constants.LightBorder,
                                borderRadius: 8,
                                overflow: 'auto',
                              }}
                              dangerouslySetInnerHTML={{ __html: tmpl.html }}
                            />
                            <ConfirmButton
                              type="button"
                              onClick={() => {
                                this.wysiwyg.changeText(tmpl.html);
                                this.setState({ showChooseTemplatePopup: false });
                              }}
                            >
                              Use template
                            </ConfirmButton>
                          </Template>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Form>
            </Column>
          </Card>
        </Popup>}
        {showChooseAssetPopup && <AssetsPopup
          onClose={() => this.setState({ showChooseAssetPopup: false })}
        />}
      </>
    );
  }
}

/** Subscribes to CreateContentPopupService and mounts the form when open. */
export class CreateContentPopup extends LogictryComponent {
  componentDidMount() {
    CreateContentPopupService.onStateUpdate(this);
  }

  componentWillUnmount() {
    CreateContentPopupService.offStateUpdate(this);
  }

  render() {
    const {
      isOpen,
      intent,
      collection,
      team,
      openTemplatesOnMount,
    } = CreateContentPopupService;

    if (!isOpen) return null;

    return (
      <CreateTemplatePopup
        initialCreateOption={intent}
        openTemplatesOnMount={openTemplatesOnMount}
        collection={collection}
        team={team}
        onClose={() => CreateContentPopupService.close()}
      />
    );
  }
}
